import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * Full-screen brand loading screen.
 *
 * Deliberately a visual twin of the static splash in `src/index.html`. That one paints while the
 * bundle is still downloading and the authentication app-initializer is blocking bootstrap on
 * `checkAuth()`; this one takes over the moment Angular is running. Keeping them identical is the
 * point: a login then reads as one continuous screen instead of blank -> splash -> "please wait".
 *
 * `statusKey` is a translation key naming whatever is currently being waited on, so the screen can
 * say which step of the login is slow rather than just spinning.
 */
@Component({
  selector: 'app-loading-screen',
  imports: [TranslatePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './loading-screen.component.html',
  styleUrl: './loading-screen.component.scss'
})
export class LoadingScreenComponent implements OnInit {

  /** Translation key for the status line. */
  readonly statusKey = input<string>('LOADING_SCREEN.PLEASE_WAIT');

  /**
   * Something the screen is waiting on has failed (InstallationSelectors.selectStartupLoadFailed), so
   * show the error state with Reload straight away instead of after failAfterMs.
   */
  readonly failed = input<boolean>(false);

  /**
   * How long to wait before treating this as stuck rather than slow.
   *
   * A safety net for waits that never resolve and report no failure. The guards stop waiting when the
   * active installation details fail, and that failure arrives through `failed`; a failed latest
   * installations list falls back to the top groups. The app shell sets a longer one: a direct load
   * waits on more round trips than a sign-in.
   */
  readonly failAfterMs = input<number>(8000);

  private readonly timedOut = signal(false);
  protected readonly hasFailed = computed(() => this.failed() || this.timedOut());

  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    const failTimer = setTimeout(() => this.timedOut.set(true), this.failAfterMs());
    this.destroyRef.onDestroy(() => clearTimeout(failTimer));
  }

  protected reload(): void {
    window.location.reload();
  }
}
